import { useState, useEffect } from "react";
import api from "../../../../utils/api";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    CircularProgress,
    Tabs,
    Tab,
    MenuItem,
    Select,
    InputLabel,
} from "@mui/material";

const languages = ['en', 'ru', 'hy'];
const languageLabels = { en: 'English', ru: 'Russian', hy: 'Armenian' };

const initialForm = () => {
    const f = { parent_id: '', order: '' };
    languages.forEach(lang => {
        f[`title_${lang}`] = '';
    });
    return f;
};

export default function CategoryModal({ open, onClose, row = null }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [form, setForm] = useState(initialForm);
    const [parentOptions, setParentOptions] = useState([]);

    const currentLanguage = languages[activeTab];

    useEffect(() => {
        if (open) {
            api.get('admin/categories/list').then((res) => {
                const list = res.data.data || [];
                setParentOptions(list.filter(c => !row || c.id !== row.id));
            }).catch(() => setParentOptions([]));
        }
    }, [open, row?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            setErrors({});

            const payload = {
                title_en: form.title_en,
                title_ru: form.title_ru,
                title_hy: form.title_hy,
                parent_id: form.parent_id === '' || form.parent_id == null ? null : Number(form.parent_id),
                order: form.order === '' || form.order == null ? null : Number(form.order),
            };

            if (row?.id) {
                await api.put(`admin/categories/${row.id}`, payload);
            } else {
                await api.post('admin/categories', payload);
            }
            onClose(true);
        } catch (e) {
            if (e.errors) setErrors(e.errors);
            else if (e.response?.data?.errors) setErrors(e.response.data.errors);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (row) {
            setForm({
                title_en: row.title_en ?? '',
                title_ru: row.title_ru ?? '',
                title_hy: row.title_hy ?? '',
                parent_id: row.parent_id ?? '',
                order: row.order ?? '',
            });
        } else {
            setForm(initialForm());
        }
        setErrors({});
        setActiveTab(0);
    }, [row, open]);

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>{!row ? "Create Category" : "Edit Category"}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                            {languages.map((lang) => (
                                <Tab key={lang} label={languageLabels[lang]} />
                            ))}
                        </Tabs>
                    </Box>

                    <FormControl error={!!errors[`title_${currentLanguage}`]?.[0]} fullWidth>
                        <FormLabel>Title ({languageLabels[currentLanguage]}) *</FormLabel>
                        <OutlinedInput
                            name={`title_${currentLanguage}`}
                            fullWidth
                            value={form[`title_${currentLanguage}`] ?? ''}
                            onChange={handleChange}
                        />
                        {errors[`title_${currentLanguage}`] && (
                            <FormHelperText sx={{ m: 0.5 }}>{errors[`title_${currentLanguage}`][0]}</FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.parent_id?.[0]} fullWidth>
                        <InputLabel id="category-parent-label">Parent category</InputLabel>
                        <Select
                            labelId="category-parent-label"
                            name="parent_id"
                            value={form.parent_id ?? ''}
                            onChange={handleChange}
                            label="Parent category"
                        >
                            <MenuItem value="">None</MenuItem>
                            {parentOptions.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                    {cat.title_en || cat.title_ru || cat.title_hy || cat.id}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.parent_id && <FormHelperText sx={{ m: 0.5 }}>{errors.parent_id[0]}</FormHelperText>}
                    </FormControl>

                    <FormControl error={!!errors.order?.[0]} fullWidth>
                        <FormLabel>Order</FormLabel>
                        <OutlinedInput
                            name="order"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={form.order ?? ''}
                            onChange={handleChange}
                            placeholder="Leave empty to add at the end"
                        />
                        {errors.order
                            ? <FormHelperText sx={{ m: 0.5 }}>{errors.order[0]}</FormHelperText>
                            : <FormHelperText sx={{ m: 0.5 }}>Lower number appears first on the courses page.</FormHelperText>}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button sx={{ color: '#fff' }} variant="contained" onClick={handleSubmit} disabled={saving}>
                    {saving ? <CircularProgress size={24} /> : (!row ? "Create" : "Update")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
